import { useEffect, useState } from "react";
import { LogOut, Package, User as UserIcon, Mail, Star, Check, Trash2, BadgeCheck, Send, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, gbp, formatApiError, photoUrl } from "@/lib/api";
import { AdminStockEditor } from "@/components/AdminStockEditor";
import { AdminSiteClosed } from "@/components/AdminSiteClosed";

export default function Account() {
  const { user, loading, login, register, logout } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [adminOrders, setAdminOrders] = useState([]);
  const [reqBusy, setReqBusy] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => {
    if (user) api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
      api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
      api.get("/admin/orders").then((r) => setAdminOrders(r.data)).catch(() => {});
      api.get("/settings/promo").then((r) => setPromo(r.data)).catch(() => {});
    }
  }, [user]);

  const savePromo = async () => {
    setPromoBusy(true);
    try {
      await api.put("/admin/settings/promo", promo);
      toast.success("Homepage banner saved");
    } catch { toast.error("Could not save banner"); }
    finally { setPromoBusy(false); }
  };

  const requestReview = async (id) => {
    setReqBusy(id);
    try {
      const { data } = await api.post(`/admin/orders/${id}/request-review`);
      setAdminOrders((prev) => prev.map((o) => (o.id === id ? { ...o, review_requested: true } : o)));
      toast.success(`Review request sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send review request");
    } finally { setReqBusy(null); }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/admin/messages/${id}/read`);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
    } catch { /* non-critical — ignore */ }
  };

  const approveReview = async (id) => {
    try {
      await api.post(`/admin/reviews/${id}/approve`);
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, approved: true } : r)));
      toast.success("Review approved — now live on the product page");
    } catch { toast.error("Could not approve review"); }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch { toast.error("Could not delete review"); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      setAdminOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success("Order deleted");
    } catch { toast.error("Could not delete order"); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this enquiry permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Enquiry deleted");
    } catch { toast.error("Could not delete enquiry"); }
  };

  const submitReply = async (id) => {
    const reply = (replyText[id] || "").trim();
    if (!reply) return;
    try {
      await api.post(`/admin/reviews/${id}/reply`, { reply });
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, admin_reply: reply } : r)));
      setReplyText((prev) => ({ ...prev, [id]: "" }));
      toast.success("Reply posted");
    } catch { toast.error("Could not post reply"); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  if (loading) return <div className="max-w-md mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</div>;

  if (user)
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-display font-black tracking-tighter">My Account</h1>
            <p className="text-muted-foreground mt-1">{user.name} · {user.email}</p>
          </div>
          <button onClick={() => { logout(); toast.success("Signed out"); }} data-testid="logout-btn"
            className="inline-flex items-center gap-2 border border-border px-4 py-2.5 rounded-sm font-medium hover:bg-muted"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>

        <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4"><Package className="w-5 h-5 text-primary" /> Order History</h2>
        {orders.length === 0 ? (
          <p className="text-muted-foreground bg-card border border-border rounded-sm p-8 text-center" data-testid="no-orders">No orders yet. Time to fix that heating!</p>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="bg-card border border-border rounded-sm p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                  <div><span className="font-mono font-bold">{o.order_number}</span> <span className="ml-2 text-xs uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-sm font-semibold">{o.status}</span></div>
                  <span className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-GB")}</span>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  {o.items.map((it, i) => <div key={`${it.slug || it.name}-${i}`} className="flex justify-between"><span>{it.qty} × {it.name}</span><span>{gbp(it.price * it.qty)}</span></div>)}
                </div>
                <div className="mt-3 pt-3 border-t border-border flex justify-between font-bold"><span>Total</span><span>{gbp(o.total)}</span></div>
              </div>
            ))}
          </div>
        )}

        {user.role === "admin" && (
          <div className="mt-12" data-testid="admin-enquiries">
            <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-primary" /> Enquiries
              {messages.some((m) => !m.read) && (
                <span className="text-xs bg-accent text-white px-2 py-0.5 rounded-sm font-semibold" data-testid="unread-count">
                  {messages.filter((m) => !m.read).length} new
                </span>
              )}
            </h2>
            {messages.length === 0 ? (
              <p className="text-muted-foreground bg-card border border-border rounded-sm p-8 text-center" data-testid="no-messages">No enquiries yet.</p>
            ) : (
              <div className="space-y-4" data-testid="messages-list">
                {messages.map((m) => (
                  <div key={m.id} className={`bg-card border rounded-sm p-5 ${m.read ? "border-border" : "border-accent/50"}`} data-testid="message-item">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                      <div>
                        <span className="font-bold">{m.name || "—"}</span>
                        {m.is_trade && <span className="ml-2 text-xs uppercase bg-amber-500/15 text-amber-700 px-2 py-0.5 rounded-sm font-semibold" data-testid="trade-badge">Trade</span>}
                        {!m.read && <span className="ml-2 text-xs uppercase bg-accent/10 text-accent px-2 py-0.5 rounded-sm font-semibold">new</span>}
                        <div className="text-sm text-muted-foreground">{m.email}</div>
                      </div>
                      <span className="text-sm text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleString("en-GB") : ""}</span>
                    </div>
                    {m.subject && <p className="mt-3 font-semibold text-sm">{m.subject}</p>}
                    <p className="mt-1 text-sm whitespace-pre-wrap">{m.message}</p>
                    <div className="mt-3 flex gap-3">
                      {m.email && <a href={`mailto:${m.email}`} className="text-sm text-primary font-semibold hover:underline" data-testid="reply-link">Reply by email</a>}
                      {!m.read && <button onClick={() => markRead(m.id)} className="text-sm text-muted-foreground hover:text-foreground" data-testid="mark-read-btn">Mark as read</button>}
                      <button onClick={() => deleteMessage(m.id)} className="text-sm text-destructive font-semibold hover:underline ml-auto" data-testid="delete-message-btn">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {user.role === "admin" && <AdminSiteClosed />}

        {user.role === "admin" && <AdminStockEditor />}

        {user.role === "admin" && promo && (
          <div className="mt-12" data-testid="admin-promo">
            <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4">
              <Megaphone className="w-5 h-5 text-primary" /> Homepage Banner
            </h2>
            <div className="bg-card border border-border rounded-sm p-6 space-y-4 max-w-2xl">
              <label className="flex items-center gap-2.5 cursor-pointer" data-testid="promo-enabled">
                <input type="checkbox" checked={!!promo.enabled} onChange={(e) => setPromo((p) => ({ ...p, enabled: e.target.checked }))} className="accent-primary w-4 h-4" />
                <span className="text-sm font-medium">Show the banner on the homepage</span>
              </label>
              <label className="block">
                <span className="text-sm font-medium">Message</span>
                <input value={promo.message || ""} onChange={(e) => setPromo((p) => ({ ...p, message: e.target.value }))} maxLength={200} placeholder="e.g. Beat the cold — free UK delivery on all heating controls this winter!" data-testid="promo-message"
                  className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium">Button label <span className="text-muted-foreground font-normal">(optional)</span></span>
                  <input value={promo.cta_label || ""} onChange={(e) => setPromo((p) => ({ ...p, cta_label: e.target.value }))} maxLength={40} placeholder="Shop TRVs" data-testid="promo-cta-label"
                    className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Button link <span className="text-muted-foreground font-normal">(optional)</span></span>
                  <input value={promo.cta_href || ""} onChange={(e) => setPromo((p) => ({ ...p, cta_href: e.target.value }))} maxLength={300} placeholder="/collections/myson-trvs" data-testid="promo-cta-href"
                    className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium">Colour</span>
                <select value={promo.theme || "accent"} onChange={(e) => setPromo((p) => ({ ...p, theme: e.target.value }))} data-testid="promo-theme"
                  className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary">
                  <option value="accent">Orange (offer)</option>
                  <option value="primary">Blue (brand)</option>
                  <option value="dark">Steel (dark)</option>
                </select>
              </label>
              <button onClick={savePromo} disabled={promoBusy} data-testid="promo-save"
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
                {promoBusy ? "Saving…" : "Save banner"}
              </button>
              <p className="text-xs text-muted-foreground">Tip: use a link starting with "/" (e.g. /collections/myson-trvs) to point at a page on your site.</p>
            </div>
          </div>
        )}

        {user.role === "admin" && (
          <div className="mt-12" data-testid="admin-orders">
            <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-primary" /> All Orders
            </h2>
            {adminOrders.length === 0 ? (
              <p className="text-muted-foreground bg-card border border-border rounded-sm p-8 text-center" data-testid="no-admin-orders">No orders yet.</p>
            ) : (
              <div className="space-y-4" data-testid="admin-orders-list">
                {adminOrders.map((o) => {
                  const email = o.shipping_details?.email;
                  return (
                    <div key={o.id} className="bg-card border border-border rounded-sm p-5" data-testid="admin-order-item">
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                        <div>
                          <span className="font-mono font-bold">{o.order_number}</span>
                          <span className="ml-2 text-xs uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-sm font-semibold">{o.status}</span>
                          {o.review_requested && <span className="ml-2 text-xs uppercase bg-emerald-500/15 text-emerald-700 px-2 py-0.5 rounded-sm font-semibold" data-testid="review-requested-badge">review requested</span>}
                          <div className="text-sm text-muted-foreground mt-1">{o.shipping_details?.name || "—"}{email ? ` · ${email}` : ""}</div>
                        </div>
                        <span className="text-sm text-muted-foreground">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""}</span>
                      </div>
                      <div className="mt-3 space-y-1.5 text-sm">
                        {o.items.map((it, i) => <div key={`${it.slug || it.name}-${i}`} className="flex justify-between"><span>{it.qty} × {it.name}</span><span>{gbp(it.price * it.qty)}</span></div>)}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border text-sm" data-testid="admin-order-address">
                        <p className="font-semibold text-foreground mb-1">Delivery address</p>
                        {(o.shipping_details?.name || o.shipping_details?.address || o.shipping_details?.postcode) ? (
                          <div className="text-muted-foreground leading-relaxed">
                            {o.shipping_details?.name && <div>{o.shipping_details.name}</div>}
                            {o.shipping_details?.address && <div>{o.shipping_details.address}</div>}
                            {(o.shipping_details?.city || o.shipping_details?.postcode) && <div>{[o.shipping_details?.city, o.shipping_details?.postcode].filter(Boolean).join(", ")}</div>}
                            {o.shipping_details?.phone && <div>Tel: {o.shipping_details.phone}</div>}
                            {email && <div>Email: {email}</div>}
                          </div>
                        ) : (
                          <p className="text-muted-foreground italic">No address recorded for this order.</p>
                        )}
                      </div>
                      <div className="mt-3 pt-3 border-t border-border flex flex-wrap items-center justify-between gap-2">
                        <span className="font-bold">Total {gbp(o.total)}</span>
                        <button
                          onClick={() => requestReview(o.id)}
                          disabled={!email || reqBusy === o.id}
                          data-testid="request-review-btn"
                          title={email ? "Email the customer a review request" : "No customer email on this order"}
                          className="inline-flex items-center gap-1.5 border border-primary text-primary px-4 py-2 rounded-sm text-sm font-semibold hover:bg-primary hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                          <Send className="w-4 h-4" /> {reqBusy === o.id ? "Sending…" : o.review_requested ? "Resend review request" : "Request review"}
                        </button>
                        <button
                          onClick={() => deleteOrder(o.id)}
                          data-testid="delete-order-btn"
                          title="Delete this order permanently"
                          className="inline-flex items-center gap-1.5 border border-destructive text-destructive px-4 py-2 rounded-sm text-sm font-semibold hover:bg-destructive hover:text-white transition-colors">
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {user.role === "admin" && (
          <div className="mt-12" data-testid="admin-reviews">
            <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-primary" /> Product Reviews
              {reviews.some((r) => !r.approved) && (
                <span className="text-xs bg-accent text-white px-2 py-0.5 rounded-sm font-semibold" data-testid="pending-reviews-count">
                  {reviews.filter((r) => !r.approved).length} pending
                </span>
              )}
            </h2>
            {reviews.length === 0 ? (
              <p className="text-muted-foreground bg-card border border-border rounded-sm p-8 text-center" data-testid="no-reviews">No reviews submitted yet.</p>
            ) : (
              <div className="space-y-4" data-testid="reviews-mod-list">
                {reviews.map((r) => (
                  <div key={r.id} className={`bg-card border rounded-sm p-5 ${r.approved ? "border-border" : "border-accent/50"}`} data-testid="review-mod-item">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                          ))}
                        </span>
                        {r.approved
                          ? <span className="text-xs uppercase bg-primary/10 text-primary px-2 py-0.5 rounded-sm font-semibold">live</span>
                          : <span className="text-xs uppercase bg-accent/10 text-accent px-2 py-0.5 rounded-sm font-semibold">pending</span>}
                      </div>
                      <span className="text-sm text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : ""}</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{r.product_name}</p>
                    {r.title && <p className="mt-1 font-semibold text-sm">{r.title}</p>}
                    <p className="mt-1 text-sm whitespace-pre-wrap">{r.body}</p>
                    {r.photos?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="mod-review-photos">
                        {r.photos.map((p) => (
                          <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-sm overflow-hidden border border-border">
                            <img src={photoUrl(p)} alt="review" className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-xs font-medium flex items-center gap-1.5">— {r.name}{r.email ? ` · ${r.email}` : ""}
                      {r.verified && <span className="inline-flex items-center gap-1 text-primary font-semibold"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>}
                    </p>
                    <div className="mt-3 flex gap-3">
                      {!r.approved && (
                        <button onClick={() => approveReview(r.id)} className="inline-flex items-center gap-1.5 text-sm text-primary font-semibold hover:underline" data-testid="approve-review-btn">
                          <Check className="w-4 h-4" /> Approve
                        </button>
                      )}
                      <button onClick={() => deleteReview(r.id)} className="inline-flex items-center gap-1.5 text-sm text-destructive hover:underline" data-testid="delete-review-btn">
                        <Trash2 className="w-4 h-4" /> Delete
                      </button>
                    </div>
                    {r.admin_reply ? (
                      <div className="mt-3 border-l-2 border-primary/30 pl-3 py-1" data-testid="mod-existing-reply">
                        <span className="text-overline text-primary">Your reply</span>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{r.admin_reply}</p>
                      </div>
                    ) : (
                      <div className="mt-3 flex gap-2 items-start">
                        <textarea
                          value={replyText[r.id] || ""}
                          onChange={(e) => setReplyText((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          placeholder="Write a public reply…" rows={1} data-testid="reply-input"
                          className="flex-1 px-3 py-2 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
                        <button onClick={() => submitReply(r.id)} disabled={!(replyText[r.id] || "").trim()} data-testid="reply-submit-btn"
                          className="inline-flex items-center gap-1.5 bg-primary text-white px-3 py-2 rounded-sm text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 transition-colors">
                          <Send className="w-4 h-4" /> Reply
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="text-center mb-8">
        <div className="grid place-items-center w-14 h-14 rounded-sm bg-primary/10 text-primary mx-auto"><UserIcon className="w-7 h-7" /></div>
        <h1 className="mt-4 text-2xl font-display font-black tracking-tight">{mode === "login" ? "Sign in" : "Create your account"}</h1>
        <p className="text-sm text-muted-foreground mt-1">{mode === "login" ? "Access your orders and details." : "Create an account in seconds."}</p>
      </div>

      <form onSubmit={submit} className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="auth-form">
        {mode === "register" && (
          <Input label="Full name" v={form.name} on={set("name")} tid="reg-name" required />
        )}
        <Input label="Email" type="email" v={form.email} on={set("email")} tid="auth-email" required />
        <Input label="Password" type="password" v={form.password} on={set("password")} tid="auth-password" required />
        {err && <p className="text-sm text-destructive" data-testid="auth-error">{err}</p>}
        <button type="submit" disabled={busy} data-testid="auth-submit"
          className="w-full bg-primary text-white py-3 rounded-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
          {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-5">
        {mode === "login" ? "New here?" : "Already have an account?"}{" "}
        <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} data-testid="toggle-auth-mode" className="text-primary font-semibold hover:underline">
          {mode === "login" ? "Create an account" : "Sign in"}
        </button>
      </p>
    </div>
  );
}

function Input({ label, v, on, type = "text", tid, required }) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <input type={type} value={v} onChange={on} data-testid={tid} required={required}
        className="mt-1.5 w-full h-11 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary" />
    </label>
  );
}
